import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { LetDirective } from '@ngrx/component';
import { BehaviorSubject, debounceTime, Observable, skip, Subscription } from 'rxjs';

export const debounceDuration: number = 2000;

@Component({
	selector: 'app-item-textarea',
	templateUrl: './item-textarea.component.html',
	styleUrl: './item-textarea.component.scss',
	imports: [LetDirective],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ItemTextAreaComponent implements OnChanges, OnDestroy {

	@Input() placeholder: string = '';
	@Input() value: string | null = null;
	@Input() maxlength: number | null = null;
	@Input() debounce: number = 0;
	@Input() emitOnInput: boolean = false;
	@Input() @HostBinding('style.--min-rows') minRows: number = 1;

	@Output() valueChange = new EventEmitter<string>();
	@Output() itemFocus = new EventEmitter();

	@ViewChild('textarea') textarea!: ElementRef<HTMLTextAreaElement>;

	inputValueBehavorSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
	inputValue$: Observable<string> = this.inputValueBehavorSubject.asObservable();

	private subscription: Subscription | undefined;
	private lastValue: string = '';

	constructor() {
	}

	// called when the inputs change
	ngOnChanges(changes: SimpleChanges) {
		// angular always passes all the input values, even if only one input changes
		if (changes['value'] && changes['value'].previousValue != changes['value'].currentValue) {
			var changedValue = changes['value'].currentValue || '';
			this.inputValueBehavorSubject.next(changedValue);
			this.lastValue = changedValue;
		}
	}

	// fires when the textarea receives focus
	onFocus() {
		if (this.debounce) {
			this.subscription = this.inputValueBehavorSubject.asObservable().pipe(skip(1), debounceTime(this.debounce)).subscribe(value => {
				this.valueChange.emit(value);
				this.lastValue = value;
			});
		}
		this.itemFocus.emit();
	}

	// fires when the value changes. Unlike the change event, it fires every time the value changes (e.g. per keystroke)
	onInput(event: Event) {
		const value = (event.target as HTMLTextAreaElement).value;
		this.inputValueBehavorSubject.next(value);
		if (this.emitOnInput) {
			this.valueChange.emit(value);
			this.lastValue = value;
		}
	}

	// fires when the textarea loses focus
	onBlur() {
		// cancel any active debounce and emit immediately (if a debounce was imminent)
		this.subscription?.unsubscribe();
		if (this.textarea.nativeElement.value !== this.lastValue) this.valueChange.emit(this.textarea.nativeElement.value);
	}

	// called by the parent component to set focus to the textarea
	focusInput() {
		this.textarea.nativeElement.focus();
	}

	ngOnDestroy(): void {
		this.inputValueBehavorSubject.complete();
	}
}